import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Activity, CheckCircle2, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { TEAM_AVATARS, TEAM_THEMES } from '../components/teamAvatars';
import { OI_PROFILES, OI_FLOW } from '../data/operatingIntelligenceData';
import { AgentMetric } from '../lib/operatingMetrics';
import LiveActivityFeed from '../components/LiveActivityFeed';
import OSLiveStats from '../components/OSLiveStats';

// Hex per-agent — sparkline accent color matches the avatar ring.
const AGENT_HEX = ['#F77F11', '#38bdf8', '#34d399', '#fbbf24', '#a78bfa', '#fb7185'];

/* =================================================================
   /operating-intelligence — Restaurant Operating Intelligence flagship.

   This page answers, in long-form, the brand question the homepage
   Operating Intelligence section asks in headline form:
     "Who is behind the intelligence?"

   It is intentionally NOT a service page. It is the panoramic brand
   surface where the six AI specialists are presented as one
   intelligence layer working together — discover → research → amplify
   → engage → coordinate → formalize.

   Structure
   ─────────
   1. SEO + JSON-LD (Organization with employees[])
   2. Hero (dark mission-control) with the six avatars as live nodes
   3. Manifesto bar — the brand statement
   4. Live grid of the 6 nodes (anchor IDs match teamAvatars keys)
   5. "How they work together" — sequence flow
   6. Per-agent deep cards — full capabilities, integrations, sample
   7. Closing CTA — Free Audit + Chat with Rachel
   ================================================================= */

const PAGE_PATH = '/operating-intelligence';

function PulseDot({ tone = 'bg-emerald-400' }) {
  return (
    <span className="relative inline-flex w-2 h-2 shrink-0">
      <span
        className={`absolute inset-0 rounded-full ${tone} opacity-60`}
        style={{ animation: 'kx-oi-pulse 2.4s ease-out infinite' }}
      />
      <span className={`relative inline-flex rounded-full w-2 h-2 ${tone}`} />
    </span>
  );
}

export default function OperatingIntelligencePage() {
  const { t, lang } = useT();
  const isEs = lang === 'es';
  const profiles = OI_PROFILES[isEs ? 'es' : 'en'];
  const flow = OI_FLOW[isEs ? 'es' : 'en'];

  // Headline & strings (kept inline — single-page surface)
  const copy = isEs
    ? {
        eyebrow: 'INTELIGENCIA OPERATIVA PARA RESTAURANTES',
        h1A: 'Una Inteligencia.',
        h1B: 'Seis Especialistas.',
        h1C: 'Siempre activos.',
        intro:
          'No somos una agencia. No somos un chatbot. Somos una capa de inteligencia operativa que coordina personas, sistemas, datos y agentes IA para hacer crecer restaurantes — todos los días, sin pausas.',
        manifesto: {
          eyebrow: 'NUESTRO PRINCIPIO',
          line:
            'Construimos para que los humanos hagan el trabajo que importa, y la inteligencia se ocupe del resto.',
        },
        liveLabel: 'LIVE',
        nowLabel: 'AHORA',
        onlineLabel: 'En línea',
        gridEyebrow: 'EQUIPO OPERATIVO',
        gridTitle: 'Conoce a los seis.',
        gridBody: 'Cada uno tiene un rol claro. Y cada uno se vuelve más poderoso cuando se conecta con el resto.',
        flowEyebrow: 'CÓMO TRABAJAN JUNTOS',
        flowTitle: 'Un solo flujo. Seis especialistas.',
        flowBody: 'Esta es la coreografía típica de un nuevo restaurante entrando en nuestra red.',
        profilesEyebrow: 'DENTRO DEL EQUIPO',
        profilesTitle: 'Un profundo vistazo a cada especialista.',
        profilesBody: 'Capacidades, integraciones y ejemplos reales de cuando cada uno entra en acción.',
        capabilitiesLabel: 'CAPACIDADES',
        integrationsLabel: 'INTEGRACIONES',
        sampleLabel: 'EJEMPLO',
        meet: 'Ver más sobre',
        closingEyebrow: 'LISTO PARA EMPEZAR',
        closingTitle: 'Activa la inteligencia operativa para tu restaurante.',
        closingBody: 'Empieza con una auditoría gratuita, o habla con Rachel ahora mismo en WhatsApp.',
        primaryCta: 'Solicitar Auditoría Gratis',
        secondaryCta: 'Hablar con Rachel',
        summary: '6 Especialistas IA en línea · Liderados por humanos · En expansión continua',
      }
    : {
        eyebrow: 'RESTAURANT OPERATING INTELLIGENCE',
        h1A: 'One Intelligence.',
        h1B: 'Six Specialists.',
        h1C: 'Always on.',
        intro:
          'We are not an agency. We are not a chatbot. We are an operating intelligence layer that coordinates people, systems, data and AI agents to grow restaurants — every day, without pauses.',
        manifesto: {
          eyebrow: 'OUR PRINCIPLE',
          line:
            'We build so humans can focus on the work that matters, and the intelligence handles the rest.',
        },
        liveLabel: 'LIVE',
        nowLabel: 'NOW',
        onlineLabel: 'Online',
        gridEyebrow: 'OPERATING TEAM',
        gridTitle: 'Meet the six.',
        gridBody: 'Each has a clear role. And each becomes more powerful when connected to the rest.',
        flowEyebrow: 'HOW THEY WORK TOGETHER',
        flowTitle: 'One flow. Six specialists.',
        flowBody: 'This is the typical choreography of a new restaurant entering our network.',
        profilesEyebrow: 'INSIDE THE TEAM',
        profilesTitle: 'A deep look at each specialist.',
        profilesBody: 'Capabilities, integrations, and real moments when each one steps in.',
        capabilitiesLabel: 'CAPABILITIES',
        integrationsLabel: 'INTEGRATIONS',
        sampleLabel: 'SAMPLE',
        meet: 'Learn more about',
        closingEyebrow: 'READY TO START',
        closingTitle: 'Activate Operating Intelligence for your restaurant.',
        closingBody: 'Start with a free audit, or talk to Rachel right now on WhatsApp.',
        primaryCta: 'Request Free Audit',
        secondaryCta: 'Talk to Rachel',
        summary: '6 AI Specialists Online · Human-led · Continuously expanding',
      };

  // JSON-LD — Organization with the six AI agents as employees
  const ldOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kitxens',
    url: 'https://kitxens.com',
    description: copy.intro,
    sameAs: [
      'https://www.linkedin.com/company/kitxens',
      'https://www.instagram.com/kitxens',
    ],
    employee: profiles.map((p) => ({
      '@type': 'Person',
      name: TEAM_AVATARS.find((a) => a.key === p.key)?.name || p.key,
      jobTitle: p.role,
      description: p.tagline,
    })),
  };

  const ldItemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: copy.gridTitle,
    itemListElement: profiles.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `${TEAM_AVATARS.find((a) => a.key === p.key)?.name} — ${p.role}`,
      url: `https://kitxens.com${PAGE_PATH}#${p.key}`,
    })),
  };

  // Map flow handoff steps to themed avatar references
  const findAvatar = (key) => TEAM_AVATARS.find((a) => a.key === key);
  const findTheme = (key) => {
    const idx = TEAM_AVATARS.findIndex((a) => a.key === key);
    return TEAM_THEMES[idx] || TEAM_THEMES[0];
  };

  return (
    <div className="bg-white" data-testid="operating-intelligence-page">
      <SEO
        path={PAGE_PATH}
        title="Restaurant Operating Intelligence"
        description={copy.intro}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ldOrganization)}</script>
        <script type="application/ld+json">{JSON.stringify(ldItemList)}</script>
      </Helmet>

      {/* Local keyframes for status pulse — confined to this page */}
      <style>{`
        @keyframes kx-oi-pulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { transform: scale(2.6); opacity: 0;    }
          100% { transform: scale(2.6); opacity: 0;    }
        }
      `}</style>

      {/* ────────────────────────────────────────────────────────── */}
      {/* HERO — mission control                                      */}
      {/* ────────────────────────────────────────────────────────── */}
      <section className="relative bg-[#0B0D12] text-white pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(247,127,17,0.18), transparent 60%)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage:
              'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 80%)',
          }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 mb-6">
              <Activity className="w-3.5 h-3.5 text-[#F77F11]" />
              <p className="text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
                {copy.eyebrow}
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-[-0.025em] leading-[1.04]">
              {copy.h1A}<br />
              {copy.h1B}<br />
              <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                {copy.h1C}
              </span>
            </h1>
            <p className="mt-7 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              {copy.intro}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href="#audit"
                className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="oi-page-primary-cta"
              >
                {copy.primaryCta} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#operating-team"
                className="inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="oi-page-secondary-cta"
              >
                {isEs ? 'Ver al equipo' : 'Meet the team'}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          {/* Hero avatar strip — six nodes pre-rendered */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-16 grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4"
          >
            {TEAM_AVATARS.map((m, i) => {
              const theme = TEAM_THEMES[i] || TEAM_THEMES[0];
              return (
                <a
                  key={m.key}
                  href={`#${m.key}`}
                  className="group flex flex-col items-center text-center rounded-2xl px-2 py-3 hover:bg-white/[0.04] transition-colors"
                  data-testid={`oi-hero-avatar-${m.key}`}
                >
                  <span
                    className={`relative inline-flex w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${theme.ring} overflow-hidden ${theme.halo} shrink-0`}
                  >
                    <img
                      src={m.img}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                    />
                  </span>
                  <p className="mt-2.5 text-[13px] font-semibold text-white tracking-[-0.005em]">
                    {m.name}
                  </p>
                  <p className="mt-0.5 text-[10px] tracking-[0.16em] uppercase font-bold text-neutral-400 group-hover:text-[#ff9b4a] transition-colors">
                    {profiles[i]?.role}
                  </p>
                </a>
              );
            })}
          </motion.div>
        </div>
      </section>

      <LiveActivityFeed />
      <OSLiveStats />

      {/* ────────────────────────────────────────────────────────── */}
      {/* MANIFESTO bar                                               */}
      {/* ────────────────────────────────────────────────────────── */}
      <section className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-12 lg:py-14">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
            {copy.manifesto.eyebrow}
          </p>
          <p className="text-2xl sm:text-3xl lg:text-[34px] font-semibold tracking-[-0.015em] leading-[1.18] text-neutral-900 max-w-4xl">
            {copy.manifesto.line}
          </p>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────── */}
      {/* OPERATING TEAM grid — live nodes                            */}
      {/* ────────────────────────────────────────────────────────── */}
      <section id="operating-team" className="bg-white py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.gridEyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.gridTitle}
            </h2>
            <p className="mt-4 text-neutral-600 text-base sm:text-[17px] leading-relaxed">
              {copy.gridBody}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((p, i) => {
              const avatar = findAvatar(p.key);
              const theme = findTheme(p.key);
              return (
                <a
                  key={p.key}
                  href={`#${p.key}`}
                  className="group rounded-2xl border border-neutral-200 bg-white hover:border-[#F77F11] hover:shadow-[0_28px_70px_-30px_rgba(247,127,17,0.4)] transition-all duration-300 p-5 sm:p-6 flex flex-col cursor-pointer"
                  data-testid={`oi-grid-${p.key}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`relative inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${theme.ring} overflow-hidden ${theme.halo} shrink-0`}
                    >
                      <img
                        src={avatar?.img}
                        alt={avatar?.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-neutral-900 text-[17px] font-semibold tracking-[-0.01em] leading-tight">
                        {avatar?.name}
                      </p>
                      <p className="mt-0.5 text-[10.5px] tracking-[0.14em] uppercase font-bold text-[#F77F11]">
                        {p.role}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 shrink-0">
                      <PulseDot tone="bg-emerald-500" />
                      <span className="text-[9.5px] tracking-[0.18em] uppercase font-bold text-emerald-700">
                        {copy.onlineLabel}
                      </span>
                    </span>
                  </div>
                  <p className="mt-5 text-neutral-700 text-[14px] leading-relaxed italic">
                    {p.tagline}
                  </p>
                  {/* Live telemetry — daily-deterministic count + ticker + sparkline */}
                  <div className="mt-5">
                    <AgentMetric
                      agentKey={p.key}
                      isEs={isEs}
                      color={AGENT_HEX[i] || '#F77F11'}
                      variant="wide"
                    />
                  </div>
                  <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between">
                    <span className="text-[12px] font-semibold text-neutral-500 group-hover:text-[#F77F11] transition-colors">
                      {copy.meet} {avatar?.name}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-[#F77F11] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────── */}
      {/* HOW THEY WORK TOGETHER — choreography                      */}
      {/* ────────────────────────────────────────────────────────── */}
      <section className="bg-neutral-50 py-24 lg:py-28 border-y border-neutral-200">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.flowEyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.flowTitle}
            </h2>
            <p className="mt-4 text-neutral-600 text-base sm:text-[17px] leading-relaxed">
              {copy.flowBody}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oi-flow-grid">
            {flow.map((step, i) => {
              const from = findAvatar(step.from);
              const to = findAvatar(step.to);
              const themeFrom = findTheme(step.from);
              const themeTo = findTheme(step.to);
              return (
                <motion.div
                  key={`${step.from}-${step.to}-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
                  data-testid={`oi-flow-step-${i}`}
                >
                  <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-400 mb-3">
                    {String(i + 1).padStart(2, '0')} · {step.label}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className={`relative inline-flex w-9 h-9 rounded-lg bg-gradient-to-br ${themeFrom.ring} overflow-hidden ${themeFrom.halo} shrink-0`}>
                      <img src={from?.img} alt={from?.name} loading="lazy" className="w-full h-full object-cover" />
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-neutral-400" />
                    <span className={`relative inline-flex w-9 h-9 rounded-lg bg-gradient-to-br ${themeTo.ring} overflow-hidden ${themeTo.halo} shrink-0`}>
                      <img src={to?.img} alt={to?.name} loading="lazy" className="w-full h-full object-cover" />
                    </span>
                    <p className="ml-2 text-[12px] font-semibold text-neutral-700 leading-tight">
                      <span className="text-neutral-900">{from?.name}</span>
                      <span className="text-neutral-400"> → </span>
                      <span className="text-neutral-900">{to?.name}</span>
                    </p>
                  </div>
                  <p className="mt-4 text-neutral-600 text-[13.5px] leading-relaxed">
                    {step.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────── */}
      {/* PROFILES — per-agent deep section                          */}
      {/* ────────────────────────────────────────────────────────── */}
      <section className="bg-white py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.profilesEyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.profilesTitle}
            </h2>
            <p className="mt-4 text-neutral-600 text-base sm:text-[17px] leading-relaxed">
              {copy.profilesBody}
            </p>
          </div>

          <div className="space-y-14 lg:space-y-20">
            {profiles.map((p, i) => {
              const avatar = findAvatar(p.key);
              const theme = findTheme(p.key);
              return (
                <motion.article
                  key={p.key}
                  id={p.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5 }}
                  className="scroll-mt-28 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start"
                  data-testid={`oi-profile-${p.key}`}
                >
                  {/* Left: avatar block */}
                  <div className="lg:col-span-4">
                    <div className={`relative inline-flex w-28 h-28 lg:w-36 lg:h-36 rounded-3xl bg-gradient-to-br ${theme.ring} overflow-hidden ${theme.halo}`}>
                      <img
                        src={avatar?.img}
                        alt={avatar?.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="mt-5 text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-neutral-900">
                      {avatar?.name}
                    </h3>
                    <p className="mt-1 text-[11px] tracking-[0.22em] uppercase font-bold text-[#F77F11]">
                      {p.role}
                    </p>
                    <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed italic">
                      {p.tagline}
                    </p>
                  </div>

                  {/* Right: content */}
                  <div className="lg:col-span-8">
                    <p className="text-neutral-700 text-[16px] sm:text-[17px] leading-relaxed">
                      {p.summary}
                    </p>

                    {/* Capabilities */}
                    <div className="mt-7">
                      <p className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
                        {copy.capabilitiesLabel}
                      </p>
                      <ul className="grid sm:grid-cols-2 gap-2.5">
                        {p.capabilities.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-[14.5px] text-neutral-800 leading-snug">
                            <CheckCircle2 className="w-4 h-4 text-[#F77F11] mt-0.5 shrink-0" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Integrations */}
                    <div className="mt-7">
                      <p className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
                        {copy.integrationsLabel}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {p.integrations.map((it) => (
                          <span
                            key={it}
                            className="inline-flex items-center text-[12.5px] font-semibold text-neutral-800 bg-neutral-100 border border-neutral-200 rounded-full px-3 py-1.5"
                          >
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Sample use */}
                    <div className="mt-7 rounded-2xl border border-neutral-200 bg-neutral-50/70 p-5">
                      <p className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#F77F11] mb-2 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        {copy.sampleLabel}
                      </p>
                      <p className="text-neutral-700 text-[14.5px] leading-relaxed">
                        {p.sampleUse}
                      </p>
                    </div>

                    {/* Live telemetry — deterministic-by-day, animates on view */}
                    <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5">
                      <AgentMetric
                        agentKey={p.key}
                        isEs={isEs}
                        color={AGENT_HEX[i] || '#F77F11'}
                        variant="profile"
                      />
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────── */}
      {/* CLOSING — bottom strip + dual CTAs                         */}
      {/* ────────────────────────────────────────────────────────── */}
      <section className="bg-[#0B0D12] text-white py-24 lg:py-32">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.closingEyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1]">
              {copy.closingTitle}
            </h2>
            <p className="mt-5 text-neutral-300 text-base sm:text-[17px] leading-relaxed max-w-2xl">
              {copy.closingBody}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#audit"
                className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="oi-page-bottom-primary-cta"
              >
                {copy.primaryCta} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/14085901102"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb955] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="oi-page-bottom-secondary-cta"
              >
                {copy.secondaryCta} <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-10 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5">
              <PulseDot tone="bg-emerald-400" />
              <span className="text-[12px] text-neutral-300 font-medium">
                {copy.summary}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
